<template>
  <div class="fin-screen">
    <div class="fin-toolbar">
      <h2 class="fin-title">{{ $t("finance_tab_earnings") }}</h2>
      <span class="fin-sub">{{ rangeText }}</span>
      <span class="fin-grow"></span>
      <finance-export :filename="exportFilename" :get-data="getExportData" :disabled="!overview" @print="shell.printPage" />
    </div>

    <finance-pending-bar :period="period" />

    <finance-state v-if="status === 'loading'" kind="loading" />
    <finance-state v-else-if="status === 'error'" kind="error" icon="mdi-alert-circle-outline" :title="$t('finance_shell.error_title')" :text="$t('finance_shell.error_text')">
      <button type="button" class="ui-btn ui-btn--dark" @click="load"><v-icon size="16">mdi-refresh</v-icon>{{ $t("finance_shell.retry") }}</button>
    </finance-state>

    <template v-else-if="overview">
      <div v-if="overview.checkFailed" class="ui-banner ui-banner--info ui-banner--warn">
        <v-icon size="16" color="currentColor">mdi-alert-outline</v-icon><span>{{ $t("finance_statement_check_warning") }}</span>
      </div>

      <finance-hero
        :balance="overview.currentBalance"
        :currency="currency"
        :settlement="settlement"
        :conflict="conflict"
        :turnover="overview.turnover || 0"
        :net="overview.net || 0"
        :deductions="overview.deductions || 0"
        :empty="empty"
        @navigate="shell.openScreen"
        @how-to-pay="openHowToPay"
      />

      <finance-state v-if="empty" kind="empty" icon="mdi-tray-full" :title="$t('finance_no_activity_in_period')" :text="$t('finance_shell.empty_period_text')">
        <button type="button" class="ui-btn" @click="thisMonth">{{ $t("finance_shell.show_this_month") }}</button>
      </finance-state>

      <template v-else>
        <div class="ui-kpis">
          <div v-for="kpi in kpiCards" :key="kpi.key" class="ui-kpi">
            <small>{{ translate(kpi.label) }}</small>
            <b class="fin-num">{{ kpi.text }}</b>
            <em v-if="kpi.trend !== null" :class="kpi.trend >= 0 ? 'is-in' : 'is-out'">{{ kpi.trend >= 0 ? "▲" : "▼" }} {{ Math.abs(kpi.trend) }} % {{ $t("finance_trend_vs_previous") }}</em>
            <em v-else>{{ kpi.note || "—" }}</em>
          </div>
        </div>

        <div class="fin-two">
          <section class="ui-card">
            <div class="ui-card__head">
              <b>{{ $t("finance_statement_for_period") }}</b>
              <small>{{ rangeText }} · {{ $t("finance_click_row_hint") }}</small>
              <span class="fin-grow"></span>
              <span v-if="overview.checkOk" class="ui-chip ui-chip--ok"><v-icon size="13" color="currentColor">mdi-check</v-icon>{{ $t("finance_statement_matches") }}</span>
            </div>
            <div class="ui-card__body">
              <finance-statement :overview="overview" clickable :active-key="activeKey" @line="openLines" />
              <p class="fin-footnote"><v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ $t("finance_posting_note") }}</p>
            </div>
          </section>

          <div class="fin-stack">
            <section class="ui-card">
              <div class="ui-card__head"><b>{{ $t("finance_turnover_by_day_title") }}</b></div>
              <div class="ui-card__body">
                <div class="fin-chart" role="img" :aria-label="$t('finance_turnover_by_day_title')">
                  <div v-for="point in series" :key="point.label" :class="{ 'is-zero': !point.value }" :title="`${shortDate(point.label)} · ${money(point.value)}`">
                    <i :style="{ height: `${point.value ? Math.max(4, Math.round((point.value / seriesMax) * 100)) : 0}%` }"></i>
                  </div>
                </div>
                <div class="fin-axis"><span v-for="tick in ticks" :key="tick">{{ tick }}</span></div>
              </div>
            </section>

            <section class="ui-card">
              <div class="ui-card__head"><b>{{ $t("finance_recent_activity_title") }}</b></div>
              <div class="ui-card__body">
                <p v-if="!overview.activity.length" class="ui-muted">{{ $t("finance_no_recent_activity") }}</p>
                <template v-else>
                  <div v-for="activity in overview.activity" :key="activity.id" class="fin-act">
                    <time>{{ shortDate(activity.at) }}</time>
                    <span>
                      <template v-for="(part, i) in activityParts(activity)">
                        <button v-if="part.orderId" :key="i" type="button" class="ui-link" @click="openOrder(part.orderId)">{{ part.text }}</button>
                        <span v-else :key="i">{{ translate(part.text) }}</span>
                      </template>
                    </span>
                    <b v-if="activity.amount !== null && activity.amount !== undefined" class="fin-num" :class="activityAmount(activity) >= 0 ? 'is-in' : 'is-out'">{{ money(activityAmount(activity), true) }}</b>
                  </div>
                  <button type="button" class="ui-link fin-link--block" @click="shell.openScreen('transactions')">{{ $t("finance_all_orders") }} →</button>
                </template>
              </div>
            </section>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import restaurantService from "@/services/restaurant";
import { fillDailySeries, formatMoney, resolveSettlement } from "@/components/restaurant/finance/constants";
import { parseOverview } from "@/components/restaurant/finance/overview";
import { formatRange, formatShortDate } from "@/components/report/shared/periods";
import FinanceExport from "../parts/FinanceExport.vue";
import FinanceHero from "../parts/FinanceHero.vue";
import FinancePendingBar from "../parts/FinancePendingBar.vue";
import FinanceState from "../parts/FinanceState.vue";
import FinanceStatement from "../parts/FinanceStatement.vue";
import LinesPanel from "../panels/LinesPanel.vue";
import OrderPanel from "../panels/OrderPanel.vue";
import HowToPayPanel from "../panels/HowToPayPanel.vue";
import { buildFinancePresets } from "../financeQuery";
import hasFinancePrint from "../hasFinancePrint";

const KPI_ORDER = ["turnover", "order_count", "commission", "subscription"];

/**
 * Zarada: veliki broj sa smjerom (sledi vam / dugujete), kartice, razrada perioda (red se otvara u panelu), promet po
 * danu i nedavne aktivnosti. Sve iz jednog odgovora GET financial-overview?from&to.
 */
export default {
  name: "FinanceEarnings",
  components: { FinanceExport, FinanceHero, FinancePendingBar, FinanceState, FinanceStatement },
  mixins: [hasFinancePrint],
  inject: { shell: "reportShell" },
  props: { period: { type: Object, required: true } },
  data: () => ({ status: "loading", overview: null, services: [], activeKey: "" }),
  computed: {
    printTitle() {
      return this.$t("finance_tab_earnings");
    },
    currency() {
      return (this.overview && this.overview.currency) || this.shell.currency;
    },
    contract() {
      const withContract = this.services.find((service) => service.is_active && service.contract && service.contract.payout_schedule);
      return withContract ? withContract.contract : null;
    },
    settlement() {
      return resolveSettlement(this.overview && this.overview.settlement, this.contract);
    },
    conflict() {
      return Boolean(this.shell.isAdmin && this.overview && this.overview.settlement && this.overview.settlement.conflict);
    },
    empty() {
      const o = this.overview;
      return Boolean(o) && !(o.turnover > 0) && o.rows.every((row) => !row.value) && !o.activity.length;
    },
    rangeText() {
      return formatRange(this.period.from, this.period.to);
    },
    kpiCards() {
      const kpis = (this.overview.kpis || []).filter((kpi) => kpi.key !== "balance");
      const ordered = KPI_ORDER.map((key) => kpis.find((kpi) => kpi.key === key)).filter(Boolean);
      const list = ordered.length ? ordered : kpis.slice(0, 4);
      const count = Number((kpis.find((kpi) => kpi.key === "order_count") || {}).value) || 0;
      const turnover = Number((kpis.find((kpi) => kpi.key === "turnover") || {}).value);
      return list.map((kpi) => {
        const value = Number(kpi.value);
        const isCount = kpi.key === "order_count" || kpi.unit === "count";
        let note = "";
        if (kpi.key === "order_count" && count && Number.isFinite(turnover)) {
          note = this.$t("finance_average_amount", { amount: this.money(turnover / count) });
        } else if (kpi.key === "commission") {
          const service = this.services.find((s) => s.is_active && s.pricing && Number(s.pricing.paying_type) === 2);
          if (service) note = `${this.translate(service.pricing.opis)} ${this.$t("finance_agreed")}`;
        } else if (kpi.key === "subscription") {
          const service = this.services.find((s) => s.is_active && s.pricing && Number(s.pricing.paying_type) === 1);
          if (service) note = this.$t("finance_schedule_monthly");
        }
        return {
          key: kpi.key,
          label: kpi.label,
          text: Number.isFinite(value) ? (isCount ? String(Math.round(value)) : this.money(value)) : String(kpi.value === undefined ? "—" : kpi.value),
          trend: kpi.trend_percent !== null && kpi.trend_percent !== undefined ? Number(kpi.trend_percent) : null,
          note,
        };
      });
    },
    series() {
      return fillDailySeries(this.overview.daily, this.period.from, this.period.to);
    },
    seriesMax() {
      return Math.max(1, ...this.series.map((point) => point.value));
    },
    ticks() {
      const list = this.series;
      if (!list.length) {
        return [];
      }
      const indexes = [...new Set([0, 0.25, 0.5, 0.75, 1].map((f) => Math.round((list.length - 1) * f)))];
      return indexes.map((i) => this.shortDate(list[i].label));
    },
    exportFilename() {
      return `zarada_${this.period.from}_${this.period.to}`;
    },
    // from i to se mijenjaju zajedno: jedan ključ = jedan zahtjev (dva posebna watchera su slala dva)
    periodKey() {
      return `${this.period.from}|${this.period.to}`;
    },
  },
  watch: {
    periodKey: "load",
  },
  created() {
    this.load();
  },
  methods: {
    money(value, signed) {
      return formatMoney(value, this.currency, { signed });
    },
    shortDate(value) {
      return value ? formatShortDate(String(value).slice(0, 10)) : "";
    },
    async load() {
      const id = this.shell.restaurantId;
      if (!id) {
        return;
      }
      this.status = "loading";
      try {
        const [res, servicesRes] = await Promise.all([
          restaurantFinanceService.getFinancialOverview(id, { from: this.period.from, to: this.period.to }),
          restaurantService.getRestaurantServices(id).catch(() => null),
        ]);
        this.overview = parseOverview((res.data && res.data.data) || res.data || {}, this.shell.currency);
        const data = servicesRes && servicesRes.data;
        this.services = (data && (data.services || (data.data && data.data.services))) || [];
        this.status = "ready";
      } catch (error) {
        this.status = "error";
      }
    },
    thisMonth() {
      const month = buildFinancePresets()[0];
      this.shell.setPeriod({ from: month.from, to: month.to });
    },
    openLines(row) {
      this.activeKey = row.key;
      this.shell.openPanel({
        key: `lines-${row.key}-${this.period.from}-${this.period.to}`,
        title: this.translate(row.label),
        subtitle: this.rangeText,
        component: LinesPanel,
        props: { restaurantId: this.shell.restaurantId, category: row.key, from: this.period.from, to: this.period.to, currency: this.currency },
        root: true,
      });
    },
    openOrder(orderId) {
      this.shell.openPanel({
        key: `order-${orderId}`,
        title: this.$t("finance_shell.order_title", { id: orderId }),
        component: OrderPanel,
        props: { orderId: Number(orderId), restaurantId: this.shell.restaurantId, currency: this.currency },
        root: true,
      });
    },
    openHowToPay() {
      this.shell.openPanel({ key: "how-to-pay", title: this.$t("finance_how_to_pay"), component: HowToPayPanel, props: {}, root: true });
    },
    // Smjer iz direction (DEBIT = na teret) ako ga backend šalje, inače predznak iznosa.
    activityAmount(activity) {
      const amount = Number(activity.amount) || 0;
      return activity.direction === "DEBIT" ? -Math.abs(amount) : amount;
    },
    // Naslov je gotov sa backenda; "#4365" u njemu postaje link na narudžbu. Iznos na kraju naslova ("... – 21.00 KM")
    // se skida kad se iznos već prikazuje desno.
    activityParts(activity) {
      let title = String(activity.title || "");
      if (activity.amount !== null && activity.amount !== undefined) {
        title = title.replace(/\s*[–-]\s*-?[\d.,]+\s*[^\s\d#]{1,4}\.?(?=\s|$)/, "");
      }
      const parts = [];
      let last = 0;
      title.replace(/#(\d+)/g, (match, id, index) => {
        if (index > last) parts.push({ text: title.slice(last, index) });
        parts.push({ text: match, orderId: id });
        last = index + match.length;
        return match;
      });
      if (last < title.length) parts.push({ text: title.slice(last) });
      if (!parts.some((p) => p.orderId) && activity.source_id) {
        parts.push({ text: ` #${activity.source_id}`, orderId: activity.source_id });
      }
      return parts;
    },
    getExportData() {
      const o = this.overview;
      const rows = [];
      if (o.opening !== null) rows.push([this.$t("finance_opening_balance"), o.opening]);
      o.rows.forEach((row) => rows.push([this.translate(row.label), row.value]));
      if (o.closing !== null) rows.push([this.$t("finance_closing_balance"), o.closing]);
      return { headers: [this.$t("finance_description"), `${this.$t("finance_amount")} (${o.currency})`], rows, numeric: [1] };
    },
  },
};
</script>
